import { View, Pressable } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"
import { Link, useLocalSearchParams, useRouter } from "expo-router"

import { ar } from "i18n/ar"
import { Header, ScrollView, Button, Text, MCIcon } from "@src/components"
import { Card, CardContent, Badge, Alert } from "@src/components/ui"
import { formatPrice, sharedStyles, formatDateForDisplay, trpc } from "@src/utils"

export default function TransactionDetailsScreen() {
  const { id } = useLocalSearchParams<{ id: string }>()
  const _router = useRouter()
  const utils = trpc.useUtils()

  const transaction = trpc.transactions.get.useQuery({ id: Number(id) })

  const cancelMutation = trpc.transactions.cancel.useMutation({
    onSuccess: () => {
      utils.transactions.list.invalidate()
      utils.transactions.get.invalidate({ id: Number(id) })
      Alert.alert(ar.success, ar.transactionCancelled)
    },
    onError: (error) => {
      Alert.alert(ar.error, error.message)
    },
  })

  if (transaction.isLoading) {
    return (
      <View className="flex-1 bg-gray-100 items-center justify-center">
        <Text>{ar.loading}</Text>
      </View>
    )
  }

  if (transaction.error || !transaction.data) {
    return (
      <View className="flex-1 bg-gray-100 items-center justify-center">
        <Text>{ar.error}</Text>
      </View>
    )
  }

  const handleCancel = () => {
    Alert.alert(ar.confirm, ar.confirmCancelTransaction, [
      { text: ar.cancel, style: "cancel" },
      { text: ar.yes, style: "destructive", onPress: () => cancelMutation.mutate({ trxId: transaction.data.id }) },
    ])
  }

  // Status badge
  let statusBadge = null
  if (transaction.data.status === "cancelled") {
    statusBadge = <Badge variant="destructive">{ar.cancelled}</Badge>
  } else if (transaction.data.status === "edited") {
    statusBadge = <Badge variant="warning">{ar.transactionEdited}</Badge>
  } else {
    statusBadge = <Badge variant="success">{ar.completed}</Badge>
  }

  return (
    <>
      <Header
        title={`${ar.receipt} #${transaction.data.id}`}
        right={
          <Link asChild href={`/dashboard/transactions/${transaction.data.id}/edit`}>
            <Pressable
              className="h-full px-2 justify-center active:opacity-70 disabled:opacity-50"
              disabled={transaction.data.status !== "normal" && transaction.data.status !== "edited"}
            >
              <MCIcon name="pencil" size={24} />
            </Pressable>
          </Link>
        }
      />

      <ScrollView onRefresh={transaction.refetch} contentContainerStyle={sharedStyles.contentContainer}>
        {transaction.data.status === "cancelled" && transaction.data.replacedBy && (
          <Link href={`/dashboard/transactions/${transaction.data.replacedBy.id}`} asChild>
            <Pressable className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 flex-row items-center justify-between active:opacity-70">
              <View>
                <Text className="text-blue-800 font-semibold text-sm">{ar.newVersion}</Text>
                <Text className="text-blue-600 text-xs">
                  {ar.transactions} #{transaction.data.replacedBy.id}
                </Text>
              </View>
              <MCIcon name="chevron-left" size={20} color="#2563EB" />
            </Pressable>
          </Link>
        )}

        {transaction.data.status === "edited" && transaction.data.replacedBy && (
          <Link href={`/dashboard/transactions/${transaction.data.replacedBy.id}`} asChild>
            <Pressable className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3 flex-row items-center justify-between active:opacity-70">
              <View>
                <Text className="text-orange-800 font-semibold text-sm">{ar.newVersion}</Text>
                <Text className="text-orange-600 text-xs">
                  {ar.transactions} #{transaction.data.replacedBy.id}
                </Text>
              </View>
              <MCIcon name="chevron-left" size={20} color="#EA580C" />
            </Pressable>
          </Link>
        )}

        {transaction.data.replaces && (
          <Link href={`/dashboard/transactions/${transaction.data.replaces.id}`} asChild>
            <Pressable className="bg-gray-50 border border-gray-300 rounded-lg p-3 mb-3 flex-row items-center justify-between active:opacity-70">
              <View>
                <Text className="text-gray-800 font-semibold text-sm">{ar.originalTransaction}</Text>
                <Text className="text-gray-600 text-xs">
                  {ar.transactions} #{transaction.data.replaces.id}
                </Text>
              </View>
              <MCIcon name="chevron-left" size={20} color="#4B5563" />
            </Pressable>
          </Link>
        )}

        {/* --- Info Card --- */}
        <Card className="mb-3">
          <CardContent className="p-3 gap-2">
            <View className="flex-row justify-between items-center">
              <View className="flex-row items-center gap-2">
                <Text className="font-bold text-lg">#{transaction.data.id}</Text>
                <Text className="text-xs">{transaction.data.createdAt ? formatDateForDisplay(transaction.data.createdAt) : ""}</Text>
              </View>
              {statusBadge}
            </View>

            {transaction.data.storagePlace ? (
              <View className="flex-row items-center gap-2">
                <MCIcon name="map-marker" size={16} color="#6B7280" />
                <Text className="text-gray-700">{transaction.data.storagePlace.name}</Text>
              </View>
            ) : null}

            {transaction.data.supplier ? (
              <Link href={`/dashboard/supplier/${transaction.data.supplier.id}`} asChild>
                <Pressable className="px-2">
                  <View className="flex-row items-center gap-2">
                    <MCIcon name="account" size={16} color="#6B7280" />
                    <Text className="text-blue-600 font-medium">{transaction.data.supplier.name}</Text>
                  </View>
                </Pressable>
              </Link>
            ) : null}
          </CardContent>
        </Card>

        <Card className="mb-3">
          <CardContent className="p-3 gap-1">
            <View className="flex-row justify-between">
              <Text className="text-gray-600 text-sm">{ar.subTotal}</Text>
              <Text className="font-medium text-sm">{formatPrice(transaction.data.subTotal)}</Text>
            </View>

            {transaction.data.discount > 0 ? (
              <View className="flex-row justify-between">
                <Text className="text-gray-600 text-sm">{ar.discount}</Text>
                <Text className="font-medium text-sm text-red-600">{`-${formatPrice(transaction.data.discount)}`}</Text>
              </View>
            ) : null}

            <View className="h-px bg-gray-200 my-1" />

            <View className="flex-row justify-between items-center">
              <Text className="font-bold">{ar.total}</Text>
              <Text className="font-bold text-lg text-green-600">{formatPrice(transaction.data.total)}</Text>
            </View>
          </CardContent>
        </Card>

        {transaction.data.notes ? (
          <Card className="mb-3 bg-yellow-50 border-yellow-200">
            <CardContent className="p-3">
              <Text className="font-medium text-sm mb-1">{ar.notes}</Text>
              <Text className="text-gray-700 text-sm italic">{transaction.data.notes}</Text>
            </CardContent>
          </Card>
        ) : null}

        <Text className="font-bold text-base mb-2">{`${ar.products} (${transaction.data.products.length})`}</Text>

        {transaction.data.products.map((item, idx) => (
          <Link href={`/dashboard/products/${item.product?.id}`} asChild key={`${item.upc}-${idx}`}>
            <Pressable className="bg-white rounded-lg border border-gray-200 p-3 mb-2 active:bg-gray-50">
              <View className="flex-row justify-between items-start mb-1">
                <View className="flex-1 mr-2">
                  <Text className="font-semibold text-gray-800">{item.product?.name}</Text>
                  <Text className="text-xs">{`${ar.sku}: ${item.upc}`}</Text>
                </View>
                <Text className="font-bold text-green-600">{formatPrice(item.cost * item.quantity)}</Text>
              </View>

              <View className="flex-row gap-2 flex-wrap">
                <Badge variant="secondary">{`${ar.qty}: ${item.quantity}`}</Badge>
                <Badge variant="secondary">{`${ar.cost}: ${formatPrice(item.cost)}`}</Badge>
                <Badge variant="secondary">{`${ar.sellPrice}: ${formatPrice(item.suggestedSalePrice)}`}</Badge>
              </View>
            </Pressable>
          </Link>
        ))}

        {(transaction.data.status === "normal" || transaction.data.status === "edited") && (
          <Button className="w-full mt-2" onPress={handleCancel} loading={cancelMutation.isPending}>
            {ar.cancelTransaction}
          </Button>
        )}

        <SafeAreaView edges={["bottom"]} />
      </ScrollView>
    </>
  )
}
